import MenuItem from './MenuItem';
import utils from '../../helpers/utils';
import {useLocation} from 'react-router-dom';
import _filter from 'lodash/filter';
import _isEmpty from 'lodash/isEmpty';
import {useSelector} from 'react-redux';
import {useContext, useEffect, useState, useMemo} from 'react';
import {IntlContext} from '../../containers/IntlProvider';
import {RootState} from '../../reducers/rootReducer';
import {useRestorationCases} from '../../customHooks/useRestoration';
import {useScamVerificationCases} from '../../customHooks/useScamVerification';
import useWhiteLabelClient from '../../customHooks/useWhiteLabelClient';
import {useSmmAccounts} from '../../customHooks/useSmmAccounts';
import {useFinancialOffers} from '../../customHooks/useFinancialOffers';
import {SmmAccountsData} from '../../types/socialMediaMonitoring';
import _isEqual from 'lodash/isEqual';
import {usePrivacyMonitorLicense} from '../../customHooks/usePrivacyMonitor';
import {useConnectionsDetails} from '../../customHooks/useConnectionsDetails';

function Menu() {
  const pathname = useLocation().pathname;
  const auth = useSelector((store: RootState) => store.auth);
  const whiteLabelClient = useWhiteLabelClient();
  const productFeatures = utils.safeGet(auth, 'user.primaryMember.plan.productFeatures', {});
  const accountId = utils.safeGet(auth, 'user.primaryMember.accountId');
  const alertListInbox = useSelector((store: RootState) => utils.safeGet(store, 'alertList.alertListInbox', []));
  const institutionsList = useSelector((store: RootState) => utils.safeGet(store, 'transactions.institutionsList', []));
  const {data: connectionsResponse} = useConnectionsDetails(accountId, productFeatures);
  const connectionsData = utils.safeGet(connectionsResponse, 'connections', []);
  const {data: smmAccounts = {} as SmmAccountsData} = useSmmAccounts(accountId);
  const {data: restorationCases} = useRestorationCases(accountId);
  const marketingDetails = utils.safeGet(auth, 'user.primaryMember.plan.marketingDetails', {});
  const showContentful = utils.safeGet(auth, 'user.primaryMember.showContentful', false);
  const {data: scamVerificationCases} = useScamVerificationCases(productFeatures);
  const userCountryCode = utils.safeGet(auth, 'user.countryCode', '');
  const {data: privacyLicense} = usePrivacyMonitorLicense(userCountryCode);
  const localizedStringBundle: StringMap = useContext(IntlContext);
  const {isMLCardOffersActive, isCreditOffersMarketplaceActive, isSavingsOffersMarketplaceActive} = useFinancialOffers(
    productFeatures,
    localizedStringBundle
  );
  const transactions = useSelector((store: RootState) => store.transactions);
  const showWarningIconUnified = utils.safeGet(transactions, 'showWarningIconUnified', false);
  const isReimagineThemeEligible = utils.isReimagineThemeEligible(auth);

  // Create a memoized version of the dynamic menu data
  const dynamicMenuData = useMemo(
    () => ({
      alertListInbox,
      institutionsList: utils.showUnifiedComponents({productFeatures}) ? connectionsData : institutionsList,
      restorationCases,
      scamVerificationCases,
      smmAccounts,
      showWarningIconUnified,
    }),
    [
      alertListInbox,
      institutionsList,
      restorationCases,
      scamVerificationCases,
      smmAccounts,
      showWarningIconUnified,
      connectionsData,
      productFeatures,
    ]
  );

  const [dspMenuList, setDspMenuList] = useState(
    utils.dspMenuList(
      productFeatures,
      marketingDetails,
      dynamicMenuData,
      whiteLabelClient,
      isMLCardOffersActive,
      privacyLicense,
      isCreditOffersMarketplaceActive || isSavingsOffersMarketplaceActive,
      showContentful,
      isReimagineThemeEligible,
      auth
    )
  );

  useEffect(() => {
    // Only update if we have actual data to show
    if (
      !_isEmpty(alertListInbox) ||
      !_isEmpty(institutionsList) ||
      !_isEmpty(restorationCases) ||
      !_isEmpty(scamVerificationCases) ||
      !_isEmpty(auth)
    ) {
      // Calculate the new menu list
      const newMenuList = utils.dspMenuList(
        productFeatures,
        marketingDetails,
        dynamicMenuData,
        whiteLabelClient,
        isMLCardOffersActive,
        privacyLicense,
        isCreditOffersMarketplaceActive || isSavingsOffersMarketplaceActive,
        showContentful,
        isReimagineThemeEligible,
        auth
      );

      // Only update state if the menu list has actually changed
      // This prevents unnecessary re-renders
      if (!_isEqual(newMenuList, dspMenuList)) {
        setDspMenuList(newMenuList);
      }
    }
  }, [
    alertListInbox,
    institutionsList,
    restorationCases,
    scamVerificationCases,
    smmAccounts,
    productFeatures,
    marketingDetails,
    whiteLabelClient,
    dynamicMenuData,
    dspMenuList,
    privacyLicense,
    showContentful,
    isReimagineThemeEligible,
    auth,
  ]);

  const filteredList = _filter(dspMenuList, (tab) => {
    // Match if pathname starts with tab.value and is either exact match or followed by / or ?
    if (pathname === tab.value || pathname.startsWith(tab.value + '/') || pathname.startsWith(tab.value + '?')) {
      return tab;
    }
  });
  const currentlySelected = filteredList.length === 0 ? undefined : (filteredList[0] as $TSFixMe).value;
  const menuItems = dspMenuList.map((listItem) => {
    return (
      <MenuItem
        href={listItem.value}
        key={listItem.name}
        iconName={listItem.iconName}
        label={localizedStringBundle[listItem.name] ? localizedStringBundle[listItem.name] : ''}
        selected={currentlySelected}
        option={listItem.option}
        whiteLabelClient={whiteLabelClient}
        supportComponent={listItem.supportComponent}
        showIconBadge={listItem.showIconBadge}
        inid={listItem.inid}
      />
    );
  });

  return (
    <div id="menuSlidesContainer" data-testid="menuSlidesContainer" className="flex flex-col">
      {menuItems}
    </div>
  );
}

export default Menu;
